import { consentService, type ConsentCategory, type ConsentSnapshot } from './consent';

type QueueFunction = ((...args: unknown[]) => void) & {
  q?: unknown[][];
  queue?: unknown[][];
  callQueue?: unknown[][];
  callMethod?: (...args: unknown[]) => void;
  sendEvent?: (...args: unknown[]) => void;
  exe?: (...args: unknown[]) => void;
  loaded?: boolean;
  version?: string;
};

interface LiveChatWidgetApi {
  _q: unknown[][];
  _h: null | ((...args: unknown[]) => void);
  _v: string;
  call(...args: unknown[]): void;
  on(...args: unknown[]): void;
  once(...args: unknown[]): void;
  off(...args: unknown[]): void;
  get(...args: unknown[]): unknown;
}

declare global {
  interface Window {
    clarity?: QueueFunction;
    fbq?: QueueFunction;
    _fbq?: QueueFunction;
    rdt?: QueueFunction;
    twq?: QueueFunction;
    Trustpilot?: { loadFromElement(element: Element, forceReload?: boolean): void };
    __lc?: Record<string, unknown>;
    LiveChatWidget?: LiveChatWidgetApi;
  }
}

const SCRIPT_IDS: Record<Exclude<ConsentCategory, 'necessary'>, string[]> = {
  functional: ['trustpilot-bootstrap'],
  analytics: ['microsoft-clarity'],
  marketing: ['meta-pixel', 'reddit-pixel', 'x-pixel'],
  support: ['livechat-widget'],
};

const COOKIE_NAMES: Record<Exclude<ConsentCategory, 'necessary'>, string[]> = {
  functional: ['wistia', 'wistia-video-progress'],
  analytics: ['ai_user', 'ai_session', '_clck', '_clsk', '_clarity'],
  marketing: ['_fbp', '_fbc', '_rdt_uuid', '_ttp', 'personalization_id', 'guest_id'],
  support: ['__lc_cid', '__lc_cst', '__livechat', 'recent_window'],
};

const loadScript = (id: string, src: string, category: ConsentCategory): Promise<void> => {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing?.dataset.loaded === 'true') return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = existing ?? document.createElement('script');
    const handleLoad = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    const handleError = () => reject(new Error(`Failed to load ${src}`));
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existing) {
      script.id = id;
      script.src = src;
      script.async = true;
      script.dataset.consentCategory = category;
      document.head.appendChild(script);
    }
  });
};

class ThirdPartyServices {
  private initialized = false;
  private activeCategories = new Set<ConsentCategory>();

  public initialize(): void {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;
    consentService.subscribe((snapshot) => this.sync(snapshot));
  }

  private sync(snapshot: ConsentSnapshot): void {
    const optionalCategories: Exclude<ConsentCategory, 'necessary'>[] = [
      'functional',
      'analytics',
      'marketing',
      'support',
    ];
    let requiresReload = false;

    for (const category of optionalCategories) {
      const allowed = snapshot.ready && snapshot.categories[category] === true;
      const active = this.activeCategories.has(category);

      if (allowed && !active) {
        this.activeCategories.add(category);
        void this.startCategory(category).catch((error) => {
          this.activeCategories.delete(category);
          console.error(`Failed to start ${category} services.`, error);
        });
      } else if (!allowed && active) {
        this.activeCategories.delete(category);
        this.clearCategory(category);
        requiresReload = true;
      }
    }

    // Third-party SDKs keep autonomous timers and queues after their script tag
    // is removed. A reload is the only reliable way to complete an in-session
    // revocation without allowing a final background event to escape.
    if (requiresReload) window.location.reload();
  }

  private async startCategory(category: Exclude<ConsentCategory, 'necessary'>): Promise<void> {
    switch (category) {
      case 'functional':
        await this.startTrustpilot();
        break;
      case 'analytics':
        await this.startClarity();
        break;
      case 'marketing':
        await Promise.all([this.startMeta(), this.startReddit(), this.startX()]);
        break;
      case 'support':
        await this.startLiveChat();
        break;
    }
  }

  private async startTrustpilot(): Promise<void> {
    await loadScript(
      'trustpilot-bootstrap',
      'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
      'functional'
    );
    document.querySelectorAll('.trustpilot-widget').forEach((element) => {
      window.Trustpilot?.loadFromElement(element, true);
    });
  }

  private async startClarity(): Promise<void> {
    if (!window.clarity) {
      const clarity: QueueFunction = (...args) => {
        (clarity.q ??= []).push(args);
      };
      window.clarity = clarity;
    }
    const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID || 'l7w1ichizh';
    await loadScript('microsoft-clarity', `https://www.clarity.ms/tag/${projectId}`, 'analytics');
  }

  private async startMeta(): Promise<void> {
    if (!window.fbq) {
      const fbq: QueueFunction = (...args) => {
        if (fbq.callMethod) fbq.callMethod(...args);
        else (fbq.queue ??= []).push(args);
      };
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = fbq;
    }
    window.fbq('consent', 'grant');
    window.fbq('init', '1798576570829190');
    window.fbq('track', 'PageView');
    await loadScript('meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js', 'marketing');
  }

  private async startReddit(): Promise<void> {
    if (!window.rdt) {
      const rdt: QueueFunction = (...args) => {
        if (rdt.sendEvent) rdt.sendEvent(...args);
        else (rdt.callQueue ??= []).push(args);
      };
      rdt.callQueue = [];
      window.rdt = rdt;
    }
    window.rdt('init', 'a2_i8go08zmgpth');
    window.rdt('track', 'PageVisit');
    await loadScript('reddit-pixel', 'https://www.redditstatic.com/ads/pixel.js', 'marketing');
  }

  private async startX(): Promise<void> {
    if (!window.twq) {
      const twq: QueueFunction = (...args) => {
        if (twq.exe) twq.exe(...args);
        else (twq.queue ??= []).push(args);
      };
      twq.version = '1.1';
      twq.queue = [];
      window.twq = twq;
    }
    window.twq('config', 'qy0lg');
    window.twq('track', 'PageView');
    await loadScript('x-pixel', 'https://static.ads-twitter.com/uwt.js', 'marketing');
  }

  private async startLiveChat(): Promise<void> {
    window.__lc = {
      ...(window.__lc ?? {}),
      license: 19440402,
      integration_name: 'manual_onboarding',
      product_name: 'livechat',
      async_init: true,
    };

    if (!window.LiveChatWidget) {
      const enqueue = (args: unknown[]) => {
        const widget = window.LiveChatWidget;
        if (!widget) return;
        if (widget._h) widget._h(...args);
        else widget._q.push(args);
      };
      window.LiveChatWidget = {
        _q: [],
        _h: null,
        _v: '2.0',
        call: (...args) => enqueue(['call', args]),
        on: (...args) => enqueue(['on', args]),
        once: (...args) => enqueue(['once', args]),
        off: (...args) => enqueue(['off', args]),
        get: (...args) => {
          enqueue(['get', args]);
          return undefined;
        },
      };
    }

    await loadScript('livechat-widget', 'https://cdn.livechatinc.com/tracking.js', 'support');
  }

  private clearCategory(category: Exclude<ConsentCategory, 'necessary'>): void {
    if (category === 'marketing') window.fbq?.('consent', 'revoke');
    if (category === 'support') window.LiveChatWidget?.call('hide');

    for (const id of SCRIPT_IDS[category]) document.getElementById(id)?.remove();
    document.querySelectorAll(`iframe[data-consent-category="${category}"]`).forEach((node) => node.remove());
    this.clearCookies(COOKIE_NAMES[category]);

    if (category === 'analytics') {
      localStorage.removeItem('claroclaim_clarity_id');
      delete window.clarity;
    } else if (category === 'functional') {
      delete window.Trustpilot;
    } else if (category === 'marketing') {
      delete window.fbq;
      delete window._fbq;
      delete window.rdt;
      delete window.twq;
    } else if (category === 'support') {
      delete window.LiveChatWidget;
      delete window.__lc;
    }
  }

  private clearCookies(names: string[]): void {
    const hostnameParts = window.location.hostname.split('.');
    const domains = new Set(['', window.location.hostname, `.${window.location.hostname}`]);
    for (let index = 1; index < hostnameParts.length - 1; index += 1) {
      domains.add(`.${hostnameParts.slice(index).join('.')}`);
    }

    for (const name of names) {
      for (const domain of domains) {
        const domainPart = domain ? `; Domain=${domain}` : '';
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domainPart}`;
      }
    }

    if (names.includes('wistia-video-progress')) {
      for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const key = localStorage.key(index);
        if (key?.startsWith('wistia-video-progress')) localStorage.removeItem(key);
      }
    }
  }
}

export const thirdPartyServices = new ThirdPartyServices();
